/**
 * Liquidity-aware Top-10 shortlist for Command Center.
 *
 * Pure presentation/ordering layer. Reads only fields the Scanner
 * already exposes. Does NOT modify Scanner discovery_score, evidence A–E,
 * candidate states, or any backend/engine behaviour.
 *
 * Liquidity classification is anchored on the market-relative
 * `activity_percentile` (already computed by the Scanner from the same
 * latest Board batch). `transaction_value`, `transaction_count` and
 * `volume` are used as supporting evidence only — their thresholds are
 * derived from observed current-batch distributions, not invented market
 * numbers. Tier priority:
 *
 *   HIGH   : activity_percentile >= 75                       (current batch top quartile)
 *   MEDIUM : activity_percentile >= 50
 *            AND transaction_value  >= 5   (billion toman)
 *            AND (transaction_count is missing OR >= 200)
 *   LOW    : everything else
 *
 * Tier evidence is returned alongside so the UI can show why a row landed
 * where it did.
 *
 * V1.GATE — Command Center eligibility gate:
 *
 *   HIGH / MEDIUM → eligible for Command Center Top 10
 *   LOW           → excluded from Command Center Top 10 only
 *                    (Market Scanner remains untouched; LOW rows are
 *                    never removed, hidden, or reranked there.)
 *
 *   Implementation split:
 *     classifyLiquidity(row)              — unchanged tier logic
 *     isCommandCenterEligible(row)       — NEW: HIGH/MEDIUM only
 *     buildCommandCenterTop10(candidates)— NEW: gate + sort + slice(10)
 *     buildLiquidityAwareTop10(...)      — kept as a thin alias on top
 *                                          of buildCommandCenterTop10 to
 *                                          avoid breaking the V1 name.
 */

/**
 * @param {object} row Scanner item
 * @returns {{tier:'HIGH'|'MEDIUM'|'LOW', evidence:object}}
 */
export function classifyLiquidity(row) {
  const evidence = {
    activity_percentile: row?.activity_percentile ?? null,
    transaction_value: row?.transaction_value ?? null,
    volume: row?.volume ?? null,
    transaction_count: row?.transaction_count ?? null,
  };
  const pct = Number(row?.activity_percentile);
  const txB = Number(row?.transaction_value);
  // For transaction_count, treat missing/null/NaN as "unknown, accept if other
  // evidence is strong"; only a concrete low number (e.g. < 200) blocks the
  // MEDIUM tier.
  const rawCount = row?.transaction_count;
  const txCountNum = typeof rawCount === 'number' ? rawCount : Number(rawCount);
  const txCountKnown = rawCount !== null && rawCount !== undefined && Number.isFinite(txCountNum);
  const txCountOK = !txCountKnown || txCountNum >= 200;

  if (Number.isFinite(pct) && pct >= 75) {
    evidence.note = 'نسبت فعالیت در چارک نخست بازار';
    return { tier: 'HIGH', evidence };
  }
  if (
    Number.isFinite(pct) && pct >= 50 &&
    Number.isFinite(txB) && txB >= 5 &&
    txCountOK
  ) {
    evidence.note = 'فعالیت متوسط با حجم معاملات کافی';
    return { tier: 'MEDIUM', evidence };
  }
  evidence.note = 'فعالیت یا حجم معاملات کمتر از آستانه مرسوم';
  return { tier: 'LOW', evidence };
}

export const LIQUIDITY_LABEL_FA = { HIGH: 'زیاد', MEDIUM: 'متوسط', LOW: 'کم' };

/**
 * Command Center Top-10 eligibility. Only HIGH and MEDIUM rows are
 * eligible. LOW rows stay visible everywhere else (Scanner, raw API
 * output) — they are simply not invited into the Command Center card.
 * Returns the tier for convenience (callers often want both answers).
 */
export function isCommandCenterEligible(row) {
  const { tier } = classifyLiquidity(row);
  return { eligible: tier === 'HIGH' || tier === 'MEDIUM', tier };
}

/**
 * Build the Liquidity-gated, deterministic Top-10 for Command Center.
 * Applies the existing liquidity classification, excludes LOW from the
 * shortlist (NOT from the Scanner pipeline — the scanner is upstream and
 * untouched), then sorts by:
 *
 *   1. opportunity_quality (desc)
 *   2. liquidity tier (asc: HIGH first)
 *   3. activity_percentile (desc)
 *   4. transaction_value (desc)
 *   5. original input order (stable deterministic tie-breaker)
 *
 * No replacement is invented if fewer than 10 candidates pass the gate.
 *
 * @param {Array<object>} candidates filtered NEW STRONG_CANDIDATE scanner items.
 * @returns {Array<object>} top ≤10 rows augmented with {liquidity:{tier,evidence}}
 */
export function buildCommandCenterTop10(candidates) {
  const list = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  // Tier priority: HIGH (0) > MEDIUM (1) > LOW (2).
  const tierOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
  const enriched = [];
  for (let idx = 0; idx < list.length; idx++) {
    const row = list[idx];
    const { tier, evidence } = classifyLiquidity(row);
    // GATE: LOW rows are not eligible for the Command Center Top 10.
    // They remain in the upstream Scanner pipeline untouched; this
    // shortlist is a presentation-only layer.
    if (tier === 'LOW') continue;
    enriched.push({
      ...row,
      // The Scanner exposes discovery_score (0–5) and shortlist_score (0–100).
      // shortlist_score may be null for items not on the scanner shortlist,
      // so fall back to discovery_score × 20 (a [0,100] map) — neither value
      // is mutated; this is purely an in-memory presentation sort key.
      opportunity_quality: typeof row.shortlist_score === 'number'
        ? row.shortlist_score
        : (Number(row.discovery_score) || 0) * 20,
      liquidity: { tier, evidence },
      _stable_index: idx,
    });
  }
  enriched.sort((a, b) => {
    // 1. opportunity_quality (desc)
    if (b.opportunity_quality !== a.opportunity_quality) return b.opportunity_quality - a.opportunity_quality;
    // 2. liquidity tier (asc: HIGH first)
    const at = tierOrder[a.liquidity.tier];
    const bt = tierOrder[b.liquidity.tier];
    if (at !== bt) return at - bt;
    // 3. activity_percentile (desc)
    const ap = Number(b.liquidity.evidence.activity_percentile) || 0;
    const bp = Number(a.liquidity.evidence.activity_percentile) || 0;
    if (ap !== bp) return ap - bp;
    // 4. transaction_value (desc)
    const atv = Number(b.liquidity.evidence.transaction_value) || 0;
    const btv = Number(a.liquidity.evidence.transaction_value) || 0;
    if (atv !== btv) return atv - btv;
    // 5. original input order (stable, deterministic).
    if (a._stable_index !== b._stable_index) return a._stable_index - b._stable_index;
    return String(a.symbol || '').localeCompare(String(b.symbol || ''));
  });
  return enriched.slice(0, 10);
}

/**
 * Backwards-compatible alias for the V1 helper name. Returns the same
 * Command Center top-10 list (HIGH/MEDIUM only, ≤10 rows).
 */
export function buildLiquidityAwareTop10(candidates) {
  return buildCommandCenterTop10(candidates);
}
